import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Users as UsersIcon, FileText, Activity, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

function StatCard({ icon: Icon, label, value, tone = "indigo", testid }) {
  const toneClasses = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    sky: "bg-sky-50 text-sky-700",
    violet: "bg-violet-50 text-violet-700",
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${toneClasses[tone]}`}>
            <Icon size={22} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
            <div className="text-3xl font-semibold text-slate-900 leading-tight">{value ?? "—"}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PlatformDashboard() {
  const [stats, setStats] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, h] = await Promise.all([
          api.get("/platform/stats"),
          api.get("/platform/hospitals"),
        ]);
        setStats(s.data);
        setHospitals(h.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Platform Overview</h1>
          <p className="text-sm text-slate-500">Multi-tenant insights across all hospitals on eMedbay.</p>
        </div>
        <Link
          to="/platform/hospitals"
          className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          data-testid="link-manage-hospitals"
        >
          Manage hospitals →
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon={Building2} label="Total Hospitals" value={stats?.total_hospitals} tone="indigo" testid="stat-total-hospitals" />
        <StatCard icon={CheckCircle2} label="Active Hospitals" value={stats?.active_hospitals} tone="emerald" testid="stat-active-hospitals" />
        <StatCard icon={UsersIcon} label="Total Users" value={stats?.total_users} tone="sky" testid="stat-total-users" />
        <StatCard icon={Activity} label="Total Patients" value={stats?.total_patients} tone="violet" testid="stat-total-patients" />
        <StatCard icon={FileText} label="Total Prescriptions" value={stats?.total_prescriptions} tone="amber" testid="stat-total-prescriptions" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Hospitals</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : hospitals.length === 0 ? (
            <div className="text-sm text-slate-500">No hospitals yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500 border-b">
                  <tr>
                    <th className="py-2 pr-4">Hospital</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Users</th>
                    <th className="py-2 pr-4">Patients</th>
                    <th className="py-2 pr-4">Prescriptions</th>
                  </tr>
                </thead>
                <tbody data-testid="platform-hospitals-table">
                  {hospitals.map((h) => (
                    <tr key={h.id} className="border-b last:border-0" data-testid={`hospital-row-${h.id}`}>
                      <td className="py-2 pr-4 font-medium text-slate-900">{h.hospital_name}</td>
                      <td className="py-2 pr-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${h.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{h.status}</span>
                      </td>
                      <td className="py-2 pr-4 text-slate-700">{h.user_count}</td>
                      <td className="py-2 pr-4 text-slate-700">{h.patient_count}</td>
                      <td className="py-2 pr-4 text-slate-700">{h.prescription_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
